/**
 * Live USDC borrow APR via our /api/aave-borrow proxy.
 *
 * The proxy fetches DefiLlama server-side from Vercel, which solves two issues
 * the previous direct-from-browser approach hit:
 *   - User-network restrictions blocking DefiLlama (Vercel can reach it fine)
 *   - Browser CORS / 4xx variability across DefiLlama edge nodes
 *
 * In local dev, Vite proxies /api/* to the deployed Vercel URL, so the same
 * endpoint works against the production functions without `vercel dev`.
 *
 * If the user runs Aave v4 once it ships USDC, they can change `project: 'aave-v4'`
 * in the inputs and the proxy will pick up the new pool.
 */

import { Decimal } from '../../engine/money.js';
import type { LiveAaveRateSnapshot } from '../../engine/types.js';

const AAVE_BORROW_PROXY = '/api/aave-borrow';

export interface DefiLlamaQuery {
  project: string;       // e.g. 'aave-v3', 'aave-v2', 'morpho-aave-v3'
  symbol: string;        // e.g. 'USDC'
  chain: string;         // e.g. 'Ethereum', 'Arbitrum'
}

export const DEFAULT_DEFILLAMA_QUERY: DefiLlamaQuery = {
  project: 'aave-v3',
  symbol: 'USDC',
  chain: 'Ethereum',
};

interface AaveBorrowResponse {
  borrowApr: number;
  market: string;
  totalSupplyUsd?: number | null;
  totalBorrowUsd?: number | null;
  /** Decimal (0.78 = 78%). Optional  -  present when upstream exposes it. */
  liquidationThreshold?: number;
  /** Decimal (0.75 = 75%). Optional. */
  loanToValue?: number;
  /** Where this snapshot ultimately came from. The proxy returns the name of
   *  the actual upstream data source (e.g. 'th3nolo/aave-v3-data'), not the
   *  proxy itself, so the UI can label "synced from <real source>". */
  source?: string;
  fetchedAtMs: number;
}

export class DefiLlamaProvider {
  /**
   * Fetch the current USDC borrow APR (variable rate) via our edge proxy.
   * Returns the snapshot wrapped with a fetchedAt timestamp for cache freshness display.
   */
  async fetchUsdcBorrowApr(query: DefiLlamaQuery = DEFAULT_DEFILLAMA_QUERY): Promise<LiveAaveRateSnapshot> {
    const params = new URLSearchParams({
      project: query.project,
      symbol: query.symbol,
      chain: query.chain,
    });
    const res = await fetch(`${AAVE_BORROW_PROXY}?${params.toString()}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      // Try to surface the upstream error text if present.
      let detail = `HTTP ${res.status}`;
      try {
        const body = (await res.json()) as { error?: string };
        if (body?.error) detail = body.error;
      } catch { /* ignore */ }
      throw new Error(`Aave borrow proxy: ${detail}`);
    }
    const json = (await res.json()) as AaveBorrowResponse;
    return {
      borrowApr: new Decimal(json.borrowApr),
      market: json.market,
      fetchedAt: new Date(json.fetchedAtMs),
      // Use the upstream source name surfaced by the proxy (e.g.
      // 'th3nolo/aave-v3-data'). Fall back to a generic 'aave-v3' label if
      // the proxy didn't provide one.
      source: json.source ?? 'aave-v3',
      ...(json.liquidationThreshold !== undefined && { liquidationThreshold: new Decimal(json.liquidationThreshold) }),
      ...(json.loanToValue !== undefined && { loanToValue: new Decimal(json.loanToValue) }),
    };
  }
}
